import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { ScanSearchIcon, GitBranchIcon, PhoneIncomingIcon, Link2Icon, ShieldCheckIcon, ToggleLeftIcon, PlugIcon, PuzzleIcon, PhoneCallIcon } from 'lucide-react';
import { Scanline } from './Scanline';
const featureBlocks = [{
  icon: ScanSearchIcon,
  code: 'SF.01',
  title: "Knows who you're talking to — before you say hello",
  description: 'The Recall Companion browser extension recognizes the Salesforce record on your screen the moment a call starts. Reviewing a Loan? Recall links the note to that borrower automatically.'
}, {
  icon: GitBranchIcon,
  code: 'SF.02',
  title: 'Built for how mortgage teams actually work',
  description: "Recall understands your pipeline, not just your address book. Whether your team lives in Leads, Contacts, or Loan opportunities, Recall resolves the conversation to the right borrower — and creates the borrower record for you if it's a brand-new prospect."
}, {
  icon: PhoneIncomingIcon,
  code: 'SF.03',
  title: "Catches calls your microphone can't",
  description: 'Dialing through a browser softphone inside Salesforce? Recall detects the call and offers to start a note — even when the conversation never touches a traditional dialer.'
}, {
  icon: Link2Icon,
  code: 'SF.04',
  title: 'One borrower, everywhere',
  description: 'Recall remembers the link between your Salesforce records and your call history. View the same borrower from their Contact or their Loan — your notes, transcripts, and AI memory follow them either way.'
}, {
  icon: ShieldCheckIcon,
  code: 'SF.05',
  title: 'Enterprise-grade by design',
  description: "Built on Salesforce's official integration platform — your admin controls exactly what Recall can see, with Salesforce's own permissions, audit trails, and security model enforced on every request. Credentials are encrypted at rest. Nothing is screen-scraped."
}, {
  icon: ToggleLeftIcon,
  code: 'SF.06',
  title: 'Completely optional. Always in your control.',
  description: "The Salesforce integration is off by default and takes one toggle to enable. Don't use Salesforce? Recall works fully without it — same capture, same AI memory, same workflow."
}];
const steps = [{
  icon: PlugIcon,
  index: '01',
  title: 'Connect',
  description: 'Your admin links your Salesforce org once; you sign in with your own Salesforce account.'
}, {
  icon: PuzzleIcon,
  index: '02',
  title: 'Install',
  description: 'Add the Recall Companion extension to Chrome and pair it with one click.'
}, {
  icon: PhoneCallIcon,
  index: '03',
  title: 'Talk',
  description: 'Take calls like you always do. Recall links every note to the right borrower, automatically.'
}];
export function Salesforce() {
  return <section id="salesforce" className="relative overflow-hidden scroll-mt-16 border-t border-slate-100 texture-dots py-20 lg:py-28" data-id="element-342">
      <Scanline data-id="element-343" />
      <span aria-hidden="true" className="font-mono-brand pointer-events-none absolute -left-10 top-1/2 -translate-y-1/2 -rotate-90 text-[10rem] font-bold tracking-tighter text-slate-900/[0.04] lg:text-[14rem]" data-id="element-344">
        CONNECTED
      </span>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-345">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="mx-auto max-w-3xl text-center" data-id="element-346">
          <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-347">
            // works_with_salesforce
          </p>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl" data-id="element-348">
            Your CRM knows the borrower. Now your call notes do too.
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-600" data-id="element-349">
            Recall connects to your Salesforce org and your browser, so every
            call note attaches itself to the right borrower — the Contact, Lead,
            or Loan you're already looking at. No searching, no typing, no "who
            was that again?"
          </p>
          <p className="font-mono-brand mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-500" data-id="element-350">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-600/70" data-id="element-351" />
            works with Salesforce · off by default · one toggle to enable
          </p>
        </motion.div>

        {/* Feature blocks */}
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-352">
          {featureBlocks.map((f, i) => <motion.div key={f.code} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: 0.05 + i % 3 * 0.08
        }} className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-353">
              <span className="font-mono-brand absolute right-5 top-5 text-[10px] tracking-widest text-slate-400" data-id="element-354">
                {f.code}
              </span>
              <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-teal-600/30 bg-teal-50" data-id="element-355">
                <f.icon className="h-5 w-5 text-teal-600" aria-hidden="true" data-id="element-356" />
              </span>
              <h3 className="font-display mt-4 text-base font-bold text-ink" data-id="element-357">
                {f.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-358">
                {f.description}
              </p>
            </motion.div>)}
        </div>

        {/* How it works — 3 steps */}
        <div className="relative mt-16" data-id="element-359">
          <motion.div initial={{
          scaleX: 0
        }} whileInView={{
          scaleX: 1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1.2,
          ease: 'easeInOut'
        }} className="absolute left-0 top-[22px] hidden h-px w-full origin-left bg-teal-600/30 lg:block" aria-hidden="true" data-id="element-360" />
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-3" data-id="element-361">
            {steps.map((step, i) => <motion.div key={step.title} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: 0.2 + i * 0.15
          }} className="relative" data-id="element-362">
                <div className="flex items-center gap-3" data-id="element-363">
                  <span className="relative z-10 flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-teal-600/30 bg-white shadow-sm" data-id="element-364">
                    <step.icon className="h-5 w-5 text-teal-600" aria-hidden="true" data-id="element-365" />
                  </span>
                  <span className="font-mono-brand text-xs font-bold tracking-widest text-slate-400" data-id="element-366">
                    {step.index} / 03
                  </span>
                </div>
                <h3 className="font-display mt-4 text-base font-bold text-ink" data-id="element-367">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-368">
                  {step.description}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* Closing line */}
        <motion.p initial={{
        opacity: 0,
        scale: 0.96
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.3,
        ease: 'easeOut'
      }} className="font-display mx-auto mt-16 max-w-2xl text-center text-xl font-bold text-ink lg:text-2xl" data-id="element-369">
          Less time logging calls. More time closing loans.{' '}
          <span className="text-teal-600" data-id="element-370">
            Recall + Salesforce — borrower memory that files itself.
          </span>
        </motion.p>
      </div>
    </section>;
}